// PoolEditModal.tsx - Updated to only edit scoring, not round names
import React, { useState, useEffect } from 'react';
import { normalizeTeamNameForComparison } from '../utils/teamUtils';
import teamNameMap from '../data/teamNameMap.json';
import { useConfig } from '../context/ConfigContext';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';

interface PoolEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  poolName: string;
  poolData: string;
  pointsConfig?: {
    pointValues: number[]; // Only point values, no round names
  };
  onSave: (newName: string, newData: string, newPointsConfig: {pointValues: number[]}) => void;
  isCreateMode?: boolean;
  existingPoolNames?: string[];
}

// Default point values for 6 rounds
const DEFAULT_POINT_VALUES = [1, 2, 4, 8, 16, 32];

const PoolEditModal: React.FC<PoolEditModalProps> = ({
  isOpen,
  onClose,
  poolName,
  poolData,
  pointsConfig,
  onSave,
  isCreateMode = false,
  existingPoolNames = []
}) => {
  const [editedName, setEditedName] = useState<string>(poolName);
  const [editedData, setEditedData] = useState<string>(poolData);
  const [editedPointValues, setEditedPointValues] = useState<number[]>(DEFAULT_POINT_VALUES);
  const [isValidFormat, setIsValidFormat] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [validationInfo, setValidationInfo] = useState<string>('');

  // Get round names from context (read-only)
  const { roundConfigs } = useConfig();

  // Reset state when modal opens/closes or props change
  useEffect(() => {
    if (isOpen) {
      setEditedName(poolName);
      setEditedData(poolData);

      // Set point configuration from props or use defaults
      if (pointsConfig && pointsConfig.pointValues) {
        setEditedPointValues([...pointsConfig.pointValues]);
      } else {
        setEditedPointValues([...DEFAULT_POINT_VALUES]);
      }

      validateInput(poolData);
    }
  }, [isOpen, poolName, poolData, pointsConfig]);

  // Validation logic for player picks
  const validateInput = (text: string) => {
    if (!text.trim()) {
      setIsValidFormat(true);
      setErrorMessage('');
      setValidationInfo('');
      return;
    }

    const playerPicksRegex = /^"([^"]+)"\s*=\s*[\w\s.,'\-&]+$/;
    const lines = text.trim().split('\n');
    const playerNames: string[] = [];

    let isValid = true;
    let error = '';
    let playerCount = 0;

    for (const line of lines) {
      if (!line.trim()) continue;

      if (!playerPicksRegex.test(line)) {
        isValid = false;
        error = `Invalid format in line: "${line}". Expected format: "PlayerName" = Team1,Team2,Team3..."`;
        break;
      }

      const match = line.match(/^"([^"]+)"\s*=/);
      if (match && match[1]) {
        playerNames.push(match[1]);
        playerCount++;
      }

      // Get all team picks
      const picks = line.split('=')[1].split(',');
      const trimmedPicks = picks.map(pick => pick.trim()).filter(pick => pick.length > 0);

      // Validate each team name
      for (const pick of trimmedPicks) {
        const normalizedPick = normalizeTeamNameForComparison(pick);

        if (!(normalizedPick in teamNameMap)) {
          isValid = false;
          error = `Unknown team "${pick}" in picks for player "${match?.[1]}". Please use valid team names.`;
          break;
        }
      }

      if (!isValid) break;

      // Validate pick count (still 63 picks for 64-team tournament)
      if (trimmedPicks.length < 63) {
        isValid = false;
        error = `Not enough team picks for player "${match?.[1]}". Expected 63 picks for a 64-team tournament, but found ${trimmedPicks.length}.`;
        break;
      }
    }

    // Check if we have at least one player
    if (isValid && playerNames.length === 0) {
      isValid = false;
      error = 'No valid player picks found.';
    }

    setIsValidFormat(isValid);
    setErrorMessage(error);

    // Set validation info
    if (isValid && playerCount > 0) {
      setValidationInfo(`${playerCount} player${playerCount > 1 ? 's' : ''} found, 63 picks each, all team names valid`);
    } else {
      setValidationInfo('');
    }
  };

  const handleDataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newData = e.target.value;
    setEditedData(newData);
    validateInput(newData);
  };

  // Handle point value changes
  const handlePointValueChange = (index: number, newValue: string) => {
    const value = parseInt(newValue, 10);
    if (!isNaN(value) && value >= 0) {
      const updatedValues = [...editedPointValues];
      updatedValues[index] = value;
      setEditedPointValues(updatedValues);
    }
  };

  // Validate point configuration
  const validatePointConfig = (): boolean => {
    // Check that all point values are valid positive integers
    if (editedPointValues.some(value => value < 0 || !Number.isInteger(value))) {
      setErrorMessage('All point values must be non-negative integers');
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!editedName.trim()) {
      setErrorMessage('Please enter a pool name');
      return;
    }

    // Check for duplicate pool name (case-insensitive), allowing the current name
    const trimmedName = editedName.trim();
    const isDuplicate = existingPoolNames.some(
      name => name.toLowerCase() === trimmedName.toLowerCase() && name.toLowerCase() !== poolName.toLowerCase()
    );
    if (isDuplicate) {
      setErrorMessage('Pool name already exists. Delete or rename existing pool first.');
      return;
    }

    if (!isValidFormat) {
      return; // Don't save if player picks validation failed
    }

    if (!validatePointConfig()) {
      return; // Don't save if point configuration validation failed
    }

    // Create the points configuration object (only point values now)
    const newPointsConfig = {
      pointValues: [...editedPointValues]
    };

    onSave(editedName.trim(), editedData, newPointsConfig);
    onClose();
  };

  const handleCancel = () => {
    // Reset to original values
    setEditedName(poolName);
    setEditedData(poolData);
    if (pointsConfig) {
      setEditedPointValues([...pointsConfig.pointValues]);
    } else {
      setEditedPointValues([...DEFAULT_POINT_VALUES]);
    }
    setErrorMessage('');
    setValidationInfo('');
    onClose();
  };

  // Dynamic text based on create vs edit mode
  const modalTitle = isCreateMode ? 'Create New Pool' : `Edit Pool: ${poolName}`;
  const saveButtonText = isCreateMode ? 'Create Pool' : 'Save Changes';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] sm:max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-5 pt-5 pb-4 border-b">
          <DialogTitle className="text-xl">{modalTitle}</DialogTitle>
          <DialogDescription>
            {isCreateMode
              ? 'Configure your new pool with player picks and scoring.'
              : 'Edit pool name, scoring configuration, and player picks.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto px-5 py-5">
          {/* Pool Name */}
          <div className="mb-5">
            <label className="block mb-2 font-medium text-base text-foreground">Pool Name</label>
            <Input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Enter pool name"
            />
          </div>

          {/* Scoring Configuration */}
          <div className="mb-5">
            <label className="block mb-2 font-medium text-base text-foreground">Scoring Configuration</label>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Round</TableHead>
                  <TableHead>Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roundConfigs.map((config, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {config.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        className="w-20 text-center"
                        value={editedPointValues[index] || 0}
                        onChange={(e) => handlePointValueChange(index, e.target.value)}
                        min="0"
                        placeholder="0"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Player Picks */}
          <div className="mb-5">
            <label className="block mb-2 font-medium text-base text-foreground">Player Picks</label>
            <textarea
              className={cn(
                "flex w-full min-h-[200px] rounded-md border bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y",
                isValidFormat ? "border-input" : "border-destructive"
              )}
              value={editedData}
              onChange={handleDataChange}
              placeholder={'Enter player picks in format:\n"Player Name" = Team1,Team2,Team3,...'}
            />
          </div>

          {/* Validation Info */}
          {validationInfo && (
            <div className="rounded-md border border-success/50 bg-success/10 p-3 mt-2 text-sm text-success">
              {validationInfo}
            </div>
          )}

        </ScrollArea>

        <DialogFooter className="px-5 py-4 border-t gap-2">
          {errorMessage && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive mr-auto">
              <strong>Error:</strong> {errorMessage}
            </div>
          )}
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValidFormat}
          >
            {saveButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PoolEditModal;
